import * as Actions from '../actions/transactionActions';
import Transaction from '../../entities/transaction';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type TransactionAction = { 
    type: string; 
    transaction: Transaction | null; 
    transactionId: string | Transaction; 
    partial: boolean; 
    lastFetch: number; 
    transactions: Transaction[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Transaction> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Transaction[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Transaction> | null, firmId: string, userId: string, updates?: Transaction[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'transaction', 'transactions', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Transaction>} data
 * @param {Transaction[]} [updates]
 * @returns {ArrayMap<Transaction>}
 */
function updateStorage(data: ArrayMap<Transaction>, updates?: Transaction[]): ArrayMap<Transaction> {
	if (logging) console.debug('⮂ transactionReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Transaction[]} [updates]
 * @returns {ArrayMap<Transaction>}
 */
function updateStorageNoReturn(updates?: Transaction[]): void {
	if (logging) console.debug('⮂ transactionReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Transaction[]} [data]
 * @returns {ArrayMap<Transaction>}
 */
function createState(data?: Transaction[] | ArrayMap<Transaction>): ArrayMap<Transaction> {
	if (!data) return ArrayMapRegistry.clone<Transaction>('transaction', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Transaction>('transaction', data.map(e => e));
    else return ArrayMapRegistry.clone<Transaction>('transaction', data);
}

function transactions(state: ArrayMap<Transaction>, action: TransactionAction): ArrayMap<Transaction> {
	if (!state) state = createState();

	let st: ArrayMap<Transaction>;

	switch (action.type) {
		// action.transaction - the new value of transaction with id action.id
		// action.transactionId - id of the target, used if new value is null
		case Actions.TRANSACTION_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ transactionReducer: FETCH_ONE_SUCCESS', action);

			if (action.transaction === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.transactionId)) {
				st = state.clone();
				st.set(action.transaction);
				// state.clear();

				updateStorageNoReturn([action.transaction]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.transactions
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.transactions
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.transactions
			 !partial, !stateEmpty, fetchEmpty	-> D return transactions in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge transactions in state newer than those in action.transactions
		 */
		case Actions.TRANSACTION_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.transactions.length === 0;
			const fetched = action.transactions.map(transaction => new Transaction({...transaction, persisted: true}));

			if (logging) console.debug(`⮂ transactionReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} transactions with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ transactionReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ transactionReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Transaction> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Transaction[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(transaction => {
					const current = st.get(transaction.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && transaction.status === 'D') {
							// Remove it from state
							toUpdate.push(transaction);
							st.delete(transaction);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (transaction.addedOn > current.addedOn) {
								toUpdate.push(transaction);
								st.set(transaction);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (transaction.status !== 'D') {
							toUpdate.push(transaction);
							st.set(transaction);
						}
						else if (logging) console.debug('⮂ transactionReducer: partialMerge: skipping deleted transaction not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ transactionReducer: empty: transactions', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ transactionReducer: fullMerge: transactions', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localTransaction => {
                    // If this transaction has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localTransaction.persisted) {
                        const fetchedTransaction = st.get(localTransaction.id);
                        if (!fetchedTransaction || localTransaction.addedOn > fetchedTransaction.addedOn) st.set(localTransaction);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.TRANSACTION_FETCH_ONE_FAILURE:
			console.error('TRANSACTION_FETCH_ONE_FAILURE', action.transactionId, action.err);

			// No update to storage required
			return state;

		case Actions.TRANSACTION_FETCH_FAILURE:
			console.error('TRANSACTION_FETCH_FAILURE', action.transactionId, action.err);

			// No update to storage required
			return state;

		case Actions.TRANSACTION_RESTORE: {
		    let transaction = new Transaction({...action.transaction});
            st = state.clone();

            const existing = st.get(transaction.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (transaction.addedOn >= existing.addedOn) {
                    st.set(transaction);
                    return updateStorage(st, [transaction]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (transaction.status === 'D') return state;

                st.set(transaction);
                return updateStorage(st, [transaction]);
            }
        }
            
		case Actions.TRANSACTION_SAVE: {
		    // console.debug('⮂ TRANSACTION_SAVE reducer', action.transaction?.id);
		    
			// clone the transaction
			let transaction = new Transaction({...action.transaction});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new transaction in the state
			// If this is a create, add to the queue
			const _transaction = st.get(transaction.id);

			if (!_transaction) st.set(transaction);
			else {
				if (transaction.addedOn >= _transaction.addedOn) st.set(transaction);
			}

			return updateStorage(st, [transaction]);
		}

		case Actions.TRANSACTION_SAVE_MANY: {
		    if (logging) console.debug('⮂ TRANSACTION_SAVE_MANY reducer', action.transactions.map(transaction => transaction.id).join(','));
		    
			// clone the transactions
			let toProcess = action.transactions.map(transaction => new Transaction(transaction));

			st = state.clone();
			// state.clear();

			const updates: Transaction[] = [];

			toProcess.forEach(transaction => {
				// Add or update the new transaction in the state
				// If this is a create, add to the queue
				const existing = st.get(transaction.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (transaction.status !== 'D') {
						st.push(transaction);
						updates.push(transaction);
					}
					else if (logging) console.debug('⮂ TRANSACTION_SAVE_MANY: skipping deleted transaction not in state');
				}
				else {
					if (transaction.addedOn >= existing.addedOn) {
						st.set(transaction);
						updates.push(transaction);
					}
					else if (logging) console.debug('⮂ Skipping, existing transaction had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.TRANSACTION_REMOVE: {
			// clone the transaction
			const transaction = new Transaction({...action.transaction});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(transaction);

			return updateStorage(st, [transaction]);
		}

		case Actions.TRANSACTION_RESTORE_FAILURE:
		case Actions.TRANSACTION_SAVE_FAILURE: {
		    console.warn('TRANSACTION_SAVE_FAILURE reducer', action.transaction?.id);
		    if (!action.transaction?.id) {
				console.warn('TRANSACTION_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const transaction = ensure(Transaction, action.transaction);
			if (!transaction) {
				console.error('Could not find transaction after call to save');
				return state;
			}

			st = state.clone();

			const _transactionRef = state.get(transaction.id);

			if (_transactionRef && (_transactionRef.addedOn === transaction.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _transaction = new Transaction({..._transactionRef});
				_transaction.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_transaction.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('transaction', _transaction).then(r => {
						if (r.isErr()) console.error(`⮂ transactionReducer: failed to save transaction with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ transactionReducer: saved transaction with fatalError info');
					}).catch(err => console.error(`⮂ transactionReducer: failed to save transaction with fatalError info: ${err.message}`));

				st.set(_transaction);
			}

			return updateStorage(st);
		}

		case Actions.TRANSACTION_SAVE_MANY_FAILURE: {
		    console.warn('TRANSACTION_SAVE_MANY_FAILURE reducer', action.transactions.map(transaction => transaction.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.TRANSACTION_RESTORE_SUCCESS:
		case Actions.TRANSACTION_SAVE_SUCCESS: {
			if (!action.transaction?.id) {
				console.warn('TRANSACTION_SAVE_SUCCESS reducer');
                return state;
			}

			const transactionRef = state.get(action.transaction?.id);
			if (!transactionRef) {
				console.warn('Could not find transaction after call to save');
                return state;
			}

			st = state.clone();

			if (action.transaction.addedOn >= transactionRef.addedOn) {
				st.set(action.transaction);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const transaction = new Transaction({...transactionRef, persisted: true});
				st.set(transaction);
			}

			return updateStorage(st, [action.transaction]);
		}

		case Actions.TRANSACTION_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ TRANSACTION_SAVE_MANY_SUCCESS reducer', action.transactions.map(transaction => transaction.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const transactions: Transaction[] = [];

			action.transactions.forEach(e => {
				const transactionRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!transactionRef) console.warn('Could not find transaction after call to save');
				else if (e.addedOn >= transactionRef.addedOn) {
				    st.set(e);
				    transactions.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const transaction = new Transaction({...transactionRef, persisted: true});
					st.set(transaction);
					transactions.push(transaction);
				}
			});

			return updateStorage(st, transactions);
		}

		case Actions.TRANSACTION_REMOVE_FAILURE: {
		    console.warn('TRANSACTION_REMOVE_FAILURE action', action.transaction?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.TRANSACTION_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.TRANSACTION_RESTORE_MANY: {
			if (logging) console.debug('⮂ TRANSACTION_RESTORE_MANY reducer', action.transactions.map(transaction => transaction.id).join(','));

			const toProcess = action.transactions.map(transaction => new Transaction({...transaction}));
			st = state.clone();

			const updates: Transaction[] = [];

			toProcess.forEach(transaction => {
				const existing = st.get(transaction.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (transaction.addedOn >= existing.addedOn) {
						st.set(transaction);
						updates.push(transaction);
					}
					else if (logging) console.debug('⮂ TRANSACTION_RESTORE_MANY: skipping older version of transaction');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (transaction.status !== 'D') {
						st.set(transaction);
						updates.push(transaction);
					}
					else if (logging) console.debug('⮂ TRANSACTION_RESTORE_MANY: skipping deleted transaction not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.TRANSACTION_REMOVE_MANY: {
			if (logging) console.debug('⮂ TRANSACTION_REMOVE_MANY reducer', action.transactions.map(transaction => transaction.id).join(','));

			const toProcess = action.transactions.map(transaction => new Transaction({...transaction}));
			st = state.clone();

			toProcess.forEach(transaction => {
				st.delete(transaction);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default transactions;